import React, { useEffect, useMemo, useRef, useState } from 'react';

import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { debounce } from 'lodash';
import { Layer, Stage } from 'react-konva';

import requestAPI from 'utils/apiHandler';

import 'pages/Editor/Editor.scss';

import style from './Editor.module.scss';
import useItemsDefinition from './ItemsDefinitions';
import ShowSelection from './ShowSelection';
import ToolSelection from './ToolSelection';
import useMoveZoom from './useMoveZoom';

const DrawingEditor = ({ className, content, id, fileName, tools: toolsFromDb }) => {
  const [isDrawing, setIsDrawing] = useState(false);
  const editorContainerRef = useRef(null);
  const stageRef = useRef(null);
  const containerRef = useRef(null);
  const rootContainer = useRef(null);
  const isInitialRender = useRef(true);
  const toolsFromDbRef = useRef(toolsFromDb);

  // Refs to keep values fresh inside the debounced callback
  const fileNameRef = useRef(fileName);
  fileNameRef.current = fileName;
  const editorInstanceRef = useRef(null);

  const {
    childStyle,
    handleMouseDownForDrag,
    handleTouchStartForDrag,
    handleMouseMoveForDrag,
    handleTouchMoveForDrag,
    handleMouseUpForDragEnd,
    handleTouchEndForDragEnd,
    handleWheelForZoom,
  } = useMoveZoom(rootContainer, containerRef);
  const {
    isDragging,
    handleMouseDownForTool,
    handleMouseMoveForTool,
    handleMouseUpForTool,
    tool,
    setTool,
    activeToolData,
    defaultValues,
    selectedId,
    setTools,
    tools,
    position,
    renderTool,
    toolsToIgnore,
    setSelectedId,
  } = useItemsDefinition();

  const toolsRef = useRef(tools);
  toolsRef.current = tools;

  useEffect(() => {
    toolsFromDbRef.current = toolsFromDb;
    setTools(toolsFromDb);
  }, [fileName]);

  const activeTool = useMemo(() => {
    if (selectedId === -1) return tool;

    return tools[selectedId].tool;
  }, [tool, selectedId]);

  // Stable debounced save — uses refs so the closure is never stale
  const handleContentChange = useMemo(
    () =>
      debounce(async () => {
        if (!editorInstanceRef.current) return;
        await requestAPI({
          url: `review/${id}`,
          method: 'PUT',
          data: {
            id,
            content: editorInstanceRef.current.getHTML(),
            drawings: toolsRef.current,
            file: fileNameRef.current,
          },
        });
      }, 300),
    [id],
  );

  const editor = useEditor({
    extensions: [StarterKit],
    content,
    onUpdate() {
      handleContentChange();
    },
  });

  editorInstanceRef.current = editor;
  const handleResize = () => {
    if (containerRef.current && stageRef.current) {
      const containerWidth = rootContainer.current.clientWidth - 4 * 16;
      const a4Width = 8.27 * window.dpi.offsetHeight; // Approximate width of A4 paper in pixels
      let scale = containerWidth / a4Width;

      if (scale > 1) scale = 1;
      //      if (scale < 1) scale -= 0.1;

      //      stageRef.current.width(a4Width * scale);
      //     containerRef.current.firstChild.style.width = `${a4Width * scale}px;`;
      // Height is adjusted based on content
    }
  };

  useEffect(() => {
    if (editorContainerRef.current && stageRef.current) {
      const containerRect = editorContainerRef.current.getBoundingClientRect();
      stageRef.current.width(containerRect.width);
      stageRef.current.height(containerRect.height);
      //      handleResize();
    }
  }, [editorContainerRef.current, stageRef.current]);

  const handleMouseDown = e => {
    if (toolsToIgnore.indexOf[activeTool] > -1) return;
    setIsDrawing(true);
    handleMouseDownForTool(activeTool, e);
  };

  const handleMouseMove = e => {
    if (!isDrawing) return;
    handleMouseMoveForTool(activeTool, e);
  };

  const handleMouseUp = () => {
    setIsDrawing(false);

    if (tool === 'select') return;

    handleMouseUpForTool(activeTool, {});
  };
  useEffect(() => {
    // Skip initial render and the re-render caused by setTools(toolsFromDb)
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    if (tools === toolsFromDbRef.current) return;
    handleContentChange();
  }, [tools]);

  const canCallFunction = (fun, ev) => {
    if (tool === 'select') fun(ev);
  };
  const getCursor = () => {
    if (tool !== 'select') return { cursor: 'auto' };

    return { cursor: isDragging ? 'grabbing' : 'grab' };
  };

  //onWheel={ev => canCallFunction(handleWheelForZoom, ev)}
  return (
    <div
      ref={rootContainer}
      className={`${style.documentHolder} ${className}`}
      onMouseMove={ev => canCallFunction(handleMouseMoveForDrag, ev)}
      onMouseUp={ev => canCallFunction(handleMouseUpForDragEnd, ev)}
      onTouchMove={ev => canCallFunction(handleTouchMoveForDrag, ev)}
      onTouchEnd={ev => canCallFunction(handleTouchEndForDragEnd, ev)}
    >
      <ToolSelection
        tool={tool}
        onClick={selTool => {
          handleMouseUpForTool(tool, {});
          setTool(selTool);
        }}
      />
      <div
        ref={containerRef}
        className={style.editorContent}
        style={{ ...childStyle, ...getCursor() }}
        onMouseDown={ev => canCallFunction(handleMouseDownForDrag, ev)}
        onTouchStart={ev => canCallFunction(handleTouchStartForDrag, ev)}
      >
        <EditorContent
          editor={editor}
          ref={val => {
            if (!val) return;
            editorContainerRef.current = val.editorContentRef.current;
          }}
        />
        <Stage
          ref={stageRef}
          onMouseDown={handleMouseDown}
          onMousemove={handleMouseMove}
          onMouseup={handleMouseUp}
          className={style.canvas}
          style={{
            pointerEvents: tool === 'text' ? 'none' : 'auto',
          }}
        >
          <Layer>
            {toolsToIgnore.indexOf(tool) === -1 &&
              renderTool(activeTool, {
                data: activeToolData,
                ...defaultValues,
                index: selectedId,
              })}
            {tools.map((obj, index) => renderTool(obj.tool, { ...obj, index }))}
          </Layer>
        </Stage>
        {selectedId > -1 && !isDragging && (
          <>
            <ShowSelection
              menuPosition={position}
              onChange={val => {
                const toolsTmp = JSON.parse(JSON.stringify(tools));
                console.log(val, toolsTmp);
                if (val === 'delete') {
                  toolsTmp.splice(selectedId, 1);
                  setSelectedId(-1);
                }
                if (val !== 'delete') {
                  toolsTmp[selectedId][val.changed] = val.value;
                }
                setTools(toolsTmp);
              }}
              data={tools[selectedId]}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default DrawingEditor;
